import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePopoverPosition } from '../redux/Slices/popoverSlice';
import { getScrollableAncestors } from '../utils/helpers';

const usePopoverPositionUpdater = (targetId, popoverRef) => {
    const dispatch = useDispatch();
    const { props } = useSelector((state) => state.popover);

    // Check if position should remain unchanged
    const shouldKeepPosition = props?.isPopPositionChanged === false;

    const updatePosition = useCallback(() => {
        // Skip position update if we want to keep the existing position
        if (shouldKeepPosition) return;

        const target = document.getElementById(targetId);
        if (!target || !popoverRef.current) return;

        const rect = target.getBoundingClientRect(); // Target's position
        const popoverRect = popoverRef.current.getBoundingClientRect(); // Popover's size
        const viewportWidth = window.innerWidth; // Viewport width
        const viewportHeight = window.innerHeight; // Viewport height

        const spacing = 4; // Spacing between the popover and target

        // Calculate available space around the target
        const spaceTop = rect.top;
        const spaceBottom = viewportHeight - rect.bottom;
        const spaceLeft = rect.left;
        const spaceRight = viewportWidth - rect.right;


        // Determine the best position for the popover
        let top = 0;
        let left = 0;

        let positionedAncestor = target;
        while (positionedAncestor && window.getComputedStyle(positionedAncestor).position === "static") {
            positionedAncestor = positionedAncestor.parentElement;
        }

        // If no positioned ancestor found, assume it's not fixed
        const isFixed = positionedAncestor && window.getComputedStyle(positionedAncestor).position === "fixed";

        // Apply scroll offsets only if the element is NOT fixed
        const topOffset = isFixed ? 0 : window.scrollY;
        const leftOffset = isFixed ? 0 : window.scrollX;

        if (spaceBottom >= popoverRect.height) {
            // Prefer opening below
            top = rect.bottom + topOffset + spacing;
        } else if (spaceTop >= popoverRect.height) {
            // Open above if space below is insufficient
            top = rect.top + topOffset - popoverRect.height - spacing;
        } else {
            // If neither fits, fit vertically within viewport
            top = Math.max(topOffset, rect.bottom + topOffset + spacing);
            top = Math.min(top, viewportHeight + topOffset - popoverRect.height);
        }

        if (spaceRight >= popoverRect.width) {
            // Prefer opening to the right
            left = rect.right + leftOffset + spacing - rect.width;
        } else if (spaceLeft >= popoverRect.width) {
            // Open to the left if space on the right is insufficient
            left = rect.left + leftOffset - popoverRect.width - spacing + rect.width;
        } else {
            // If neither fits, fit horizontally within viewport
            left = Math.max(leftOffset, rect.left + leftOffset);
            left = Math.min(left, viewportWidth + leftOffset - popoverRect.width);
        }


        // For block action handles, position to the left of the trigger
        const isBlockHandle = targetId && targetId.startsWith('block-handle-');

        if (isBlockHandle) {
            // Position to the left of the drag handle
            left = rect.left + leftOffset - popoverRect.width - spacing;

            // If not enough space on the left, fall back to right
            if (left < leftOffset) {
                left = rect.right + leftOffset + spacing;
            }

            // Align top of popover with top of trigger
            top = rect.top + topOffset;

            // Ensure it doesn't go outside viewport vertically
            if (top + popoverRect.height > viewportHeight + topOffset) {
                top = viewportHeight + topOffset - popoverRect.height - spacing;
            }
            if (top < topOffset) {
                top = topOffset + spacing;
            }
        }

        // For sidebar items, always prefer right side positioning
        const isSidebarItem = target.closest('.zoobbe-global-sidebar');

        if (isSidebarItem) {
            // Always position to the right of sidebar items
            left = rect.right + leftOffset + spacing;

            // Ensure it doesn't go outside viewport on the right
            if (left + popoverRect.width > viewportWidth + leftOffset) {
                left = viewportWidth + leftOffset - popoverRect.width - spacing;
            }

            // Center vertically relative to the target
            top = rect.top + topOffset + (rect.height / 2) - (popoverRect.height / 2);

            // Ensure it doesn't go outside viewport vertically
            if (top < topOffset) {
                top = topOffset + spacing;
            } else if (top + popoverRect.height > viewportHeight + topOffset) {
                top = viewportHeight + topOffset - popoverRect.height - spacing;
            }
        }

        // Dispatch the position update
        dispatch(updatePopoverPosition({ top, left }));
    }, [dispatch, targetId, popoverRef, shouldKeepPosition]);




    useEffect(() => {
        if (!targetId || !popoverRef.current) return;

        const target = document.getElementById(targetId);
        const scrollableAncestors = getScrollableAncestors(target);

        updatePosition(); // Update position immediately

        // Attach scroll and resize listeners
        const handleUpdate = () => updatePosition();

        scrollableAncestors.forEach(ancestor => ancestor.addEventListener('scroll', handleUpdate));
        window.addEventListener('resize', handleUpdate);

        // Observe popover size changes
        const observer = new ResizeObserver(() => {
            requestAnimationFrame(handleUpdate);
        });
        observer.observe(popoverRef.current);

        return () => {
            scrollableAncestors.forEach(ancestor => ancestor.removeEventListener('scroll', handleUpdate));
            window.removeEventListener('resize', handleUpdate);
            observer.disconnect();
        };
    }, [targetId, updatePosition]);

    useEffect(() => {
        updatePosition(); // Recalculate position on changes
    }, [targetId, popoverRef, updatePosition]);
};

export default usePopoverPositionUpdater;
